import type { CicloFacturacion, Plan, SuscripcionDb } from "../schemas";
import type { PricingFeature } from "../schemas/ui";
import type { TFunction } from "i18next";
import type { LucideIcon } from "lucide-react";
import {
	BoxIcon,
	CheckLineIcon,
	ShootingStarIcon,
	DollarLineIcon,
} from "@/components/icons";
import { getCurrencySymbol } from "@/utils/finance";
import { formatCurrency } from "@/utils/formatters";
import { getPlanTier, isFreePlan } from "./planIdentity";

const MONTHS_PER_YEAR = 12;

/** Price of a plan broken down for display, already translated. */
export interface PlanPriceLabels {
	/** Headline figure, always per month so both cycles compare directly */
	amount: string;
	currencySymbol: string;
	/** Period suffix for the headline figure */
	period: string;
	/** Badge clarifying the charge is yearly, null on monthly billing */
	note: string | null;
	/** Real yearly charge and saving, null on monthly billing */
	caption: string | null;
}

/** Plan prices are whole soles today; only render cents when they exist. */
const formatPriceAmount = (amount: number): string =>
	Number.isInteger(amount) ? String(amount) : amount.toFixed(2);

const formatMoney = (amount: number, currency: string): string =>
	formatCurrency(amount, currency, {
		minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
		maximumFractionDigits: 2,
	});

/**
 * Determines if a plan is the user's current plan
 * @param planId - ID of the plan to check
 * @param planName - Name of the plan to check
 * @param subscription - Current subscription data
 * @returns true if the plan is the current plan
 */
export const isCurrentPlan = (
	plan: Plan,
	subscription?: SuscripcionDb | null,
): boolean => {
	if (!subscription?.plan) {
		return isFreePlan(plan);
	}

	// Check by ID first
	if (subscription.plan.id === plan.id) return true;

	// Fallback to name matching (case-insensitive)
	if (subscription.plan.nombre?.toLowerCase() === plan.nombre.toLowerCase())
		return true;

	return false;
};

/**
 * Gets the appropriate icon component for a plan based on its name
 * @param planName - Name of the plan
 * @returns Icon component type
 */
export const getPlanIcon = (planName: string): LucideIcon | typeof BoxIcon => {
	const lowerName = planName.toLowerCase();

	// Decide from the name alone. `isFreePlan` also treats a plan with no
	// provider ids and no price as free — and a bare `{ nombre }` matches that,
	// so routing through it made every plan look free and left the three
	// branches below unreachable.
	if (lowerName.includes("free") || lowerName.includes("gratis")) {
		return BoxIcon;
	}

	if (lowerName.includes("personal") || lowerName.includes("básico")) {
		return CheckLineIcon;
	}

	if (lowerName.includes("professional") || lowerName.includes("pro")) {
		return ShootingStarIcon;
	}

	// Default icon for unknown plan types
	return DollarLineIcon;
};

/**
 * Calculates the price for a plan based on billing cycle.
 * `precio_anual` is the total charged once a year, NOT a monthly rate.
 * @param plan - Plan data
 * @param cycle - Billing cycle (mensual or anual)
 * @returns Price charged per billing period
 */
export const getPlanPrice = (plan: Plan, cycle: CicloFacturacion): number => {
	return cycle === "mensual" ? plan.precio_mensual : plan.precio_anual;
};

/**
 * Whole-percent saving of paying a year upfront vs. 12 monthly charges.
 * Always derived from the real prices, never hardcoded, so the badge cannot
 * drift away from what Stripe actually charges.
 * @returns Saving in percent, or 0 for free plans / annual prices with no saving
 */
export const getAnnualDiscountPct = (plan: Plan): number => {
	const monthlyForAYear = plan.precio_mensual * MONTHS_PER_YEAR;
	if (monthlyForAYear <= 0 || plan.precio_anual <= 0) return 0;

	const pct = Math.round((1 - plan.precio_anual / monthlyForAYear) * 100);
	return pct > 0 ? pct : 0;
};

/**
 * Saving advertised on the billing toggle: the smallest one across paid plans,
 * so the badge never promises more than any single plan delivers.
 */
export const getAdvertisedAnnualDiscountPct = (plans: Plan[]): number => {
	let lowest = 0;
	for (const plan of plans) {
		const pct = getAnnualDiscountPct(plan);
		if (pct === 0) continue;
		if (lowest === 0 || pct < lowest) lowest = pct;
	}
	return lowest;
};

/**
 * Builds the price a plan should display. Both cycles are shown as a per-month
 * figure so they compare directly; the annual cycle carries the real yearly
 * charge alongside it instead of showing a yearly total labelled as monthly.
 */
export const getPlanPriceLabels = (
	plan: Plan,
	cycle: CicloFacturacion,
	t: TFunction,
): PlanPriceLabels => {
	const currency = plan.currency || "PEN";
	const currencySymbol = getCurrencySymbol(currency);
	const isAnnual = cycle === "anual" && plan.precio_anual > 0;

	if (!isAnnual) {
		return {
			amount: formatPriceAmount(plan.precio_mensual),
			currencySymbol,
			period: t("pricing:billing.perMonth"),
			note: null,
			caption: null,
		};
	}

	// escapeValue off: the amount carries a "/" (S/) that i18next would turn
	// into "&#x2F;", and React already escapes what we render.
	const annualTotal = t("pricing:billing.annualTotal", {
		amount: formatMoney(plan.precio_anual, currency),
		interpolation: { escapeValue: false },
	});
	const savings = plan.precio_mensual * MONTHS_PER_YEAR - plan.precio_anual;
	const savingsLabel =
		savings > 0
			? t("pricing:billing.annualSavings", {
					amount: formatMoney(savings, currency),
					interpolation: { escapeValue: false },
				})
			: null;

	return {
		amount: formatPriceAmount(plan.precio_anual / MONTHS_PER_YEAR),
		currencySymbol,
		period: t("pricing:billing.perMonth"),
		note: t("pricing:billing.billedAnnually"),
		caption: savingsLabel
			? `${annualTotal} \u00b7 ${savingsLabel}`
			: annualTotal,
	};
};

/**
 * Renders a countable limit as a phrase, e.g. "3 propiedades" or, when the
 * backend reports `null`, "propiedades ilimitadas". Every figure on the
 * pricing page comes through here, so the page can never advertise a limit
 * the plan does not actually grant.
 */
const limitPhrase = (
	t: TFunction,
	unit: "properties" | "mortgages" | "amortizations" | "incomes" | "expenses",
	value: number | null,
): string =>
	value === null
		? t(`pricing:units.${unit}_unlimited`)
		: t(`pricing:units.${unit}`, { count: value });

/** Interpolated fragments carry "**" emphasis markers, which must survive. */
const NO_ESCAPE = { interpolation: { escapeValue: false } } as const;

/** The projection line closes every tier's list. */
const projectionFeature = (
	t: TFunction,
	years: number | null,
): PricingFeature | null => {
	if (years === 0) return null;

	return {
		text:
			years === null
				? t("pricing:plan_features.shared.projection_unlimited")
				: t("pricing:plan_features.shared.projection", { count: years }),
		included: true,
	};
};

/**
 * The selling points shown on a pricing card, worded per tier.
 *
 * The wording is fixed copy but every number inside it is interpolated from
 * `plan.features.limits`, so the card and the entitlement the backend enforces
 * can never disagree. Text may contain `**bold**` spans, which `PricingCard`
 * renders as emphasis.
 */
export const getPricingFeatures = (
	plan: Plan,
	t: TFunction,
): PricingFeature[] => {
	const { limits, flags } = plan.features;
	const tier = getPlanTier(plan);

	const properties = limitPhrase(t, "properties", limits.properties);
	const mortgages = limitPhrase(t, "mortgages", limits.mortgages_per_property);
	const amortizations = limitPhrase(
		t,
		"amortizations",
		limits.amortizations_per_mortgage,
	);
	const incomes = limitPhrase(t, "incomes", limits.incomes_per_property);
	const expenses = limitPhrase(t, "expenses", limits.expenses_per_property);

	const features: PricingFeature[] = [];

	if (tier === "free") {
		features.push({
			text: t("pricing:plan_features.free.properties", {
				properties,
				...NO_ESCAPE,
			}),
			included: true,
		});
		features.push({
			text: t("pricing:plan_features.free.amortizations", {
				amortizations,
				...NO_ESCAPE,
			}),
			included: true,
		});
		features.push({
			text: t("pricing:plan_features.free.finances", {
				incomes,
				expenses,
				...NO_ESCAPE,
			}),
			included: true,
		});
	} else if (tier === "pro") {
		features.push({
			text: t("pricing:plan_features.pro.amortizations", {
				amortizations,
				...NO_ESCAPE,
			}),
			included: true,
		});
		features.push({
			text: t("pricing:plan_features.pro.properties", {
				properties,
				mortgages,
				...NO_ESCAPE,
			}),
			included: true,
		});

		if (flags.debt_purchase_access || flags.refinance_access) {
			features.push({
				text: t("pricing:plan_features.pro.debtPurchase"),
				included: true,
			});
		}

		const financesUnlimited =
			limits.incomes_per_property === null &&
			limits.expenses_per_property === null;
		features.push({
			text: financesUnlimited
				? t("pricing:plan_features.pro.finances_unlimited")
				: t("pricing:plan_features.pro.finances", {
						incomes,
						expenses,
						...NO_ESCAPE,
					}),
			included: true,
		});

		if (flags.valuation_access) {
			features.push({
				text: t("pricing:plan_features.pro.rentalPrice"),
				included: true,
			});
		}
	} else {
		features.push({
			text: t("pricing:plan_features.investor.everything"),
			included: true,
		});
		features.push({
			text: t("pricing:plan_features.investor.properties", {
				properties,
				mortgages,
				...NO_ESCAPE,
			}),
			included: true,
		});

		if (flags.valuation_access) {
			features.push({
				text: t("pricing:plan_features.investor.salePrice"),
				included: true,
			});
		}
	}

	const projection = projectionFeature(
		t,
		limits.dashboard_future_horizon_years,
	);
	if (projection) {
		features.push(projection);
	}

	return features;
};
